import * as React from 'react'
import styled from '@emotion/styled'
import Image from 'next/image'
import Link from 'next/link'
import { useEffectOnce } from 'react-use'

import { isEmpty } from '@livechat/data-utils'

import { usePreviewController } from '../../WidgetPreview/previewController'
import AppPropertiesForm from './AppPropertiesForm'
import Button from 'components/Button'
import CircularLoader from 'components/CircularLoader'
import { FeatureExplanation } from 'components/FeaturesList'
import Modal from 'components/Modal'
import UseAsyncFn from 'components/UseAsyncFn'
import Video from 'components/Video'
import { useAppInstances } from 'hooks/useAppInstances'
import { useApps } from 'hooks/useApps'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { addAppInstance, removeAppInstance } from 'lib/api'
import { formatDate } from 'lib/formatDate'
import { EVENT_NAMES } from 'lib/tracking'
import type { App, AppInstance } from 'types/apps'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: ${({ theme }) => theme.spaces.space7};
`

const VideoSection = styled.div`
	background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`

const StyledButton = styled(Button)`
	padding: 0;
`

const AppsListOuter = styled.div`
	display: flex;
	flex-direction: column;
	height: min-content;
`

const LogoOuter = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
`

const StatusContainer = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
`

const TileHeader = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	justify-content: space-between;
`

const TileContent = styled.div`
	width: 100%;
	margin-top: ${({ theme }) => theme.spaces.space3};
`

const TileFooter = styled.div`
	display: grid;
	align-items: center;
	width: 100%;
	grid-auto-flow: column;
`

const StatusCircle = styled.div`
	width: 12px;
	height: 12px;
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	background-color: ${({ theme }) => theme.colors.light.success};
`

const StyledImage = styled(Image)`
	border-radius: ${({ theme }) => theme.borderRadiuses.lg};
	width: 30px;
	height: 30px;
`

const Title = styled.p`
	${({ theme }) => theme.typography.paragraphMedium};
	margin-bottom: ${({ theme }) => theme.spaces.space6};
`

const Explanation = styled(FeatureExplanation)`
	margin-bottom: 0;
`

const AppItemOuter = styled.li`
	width: 100%;
	display: flex;
	flex-flow: column nowrap;
	justify-content: flex-start;
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	gap: ${({ theme }) => theme.spaces.space4};
	${({ theme }) => theme.typography.caption};
	padding: ${({ theme }) => theme.spaces.space5};
	background-color: ${({ theme }) => theme.colors.light.grayscale[15]};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};
	margin-bottom: ${({ theme }) => theme.spaces.space4};
	position: relative;
`

const AppName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	width: 200px;
`

const StatusText = styled.span`
	${({ theme }) => theme.typography.smallcaps};
	display: flex;
	align-items: center;
	color: #7a7a7a;
`

const AppDescription = styled.p`
	margin: 0;
`

const StyledLink = styled(Link)`
	font-size: ${({ theme }) => theme.fontSizes.size12};
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
	justify-self: end;
`

const AppPropertiesModal = styled(Modal)``

function AppsIntegration() {
	const showToast = useToast()
	const { apps, areAppsLoading } = useApps()
	const track = useTracking()
	const { appInstances, mutateAppInstance, areAppInstancesLoading } = useAppInstances()
	const [selectedApp, setSelectedApp] = React.useState<App | null>(null)
	const previewController = usePreviewController()

	const showInPreview = () => previewController.showView('Homescreen', 'app-card-container')
	useEffectOnce(showInPreview)

	const isAppInstalled = (id: string) => appInstances?.some(({ appId }) => appId === id)

	const hideModal = () => {
		setSelectedApp(null)
	}

	const handleAdd = async (appId: string) => {
		const app = apps?.find(({ id }) => id === appId)

		if (!app) {
			return
		}

		if (!isEmpty(app.properties)) {
			setSelectedApp(app)
			return
		}

		try {
			const data = await addAppInstance(appId)
			mutateAppInstance((instances) => [...(instances ?? []), data], { revalidate: false })
			showToast('success', 'App installed')
			track(EVENT_NAMES.APPS_ADD_CLICKED)
		} catch {
			showToast('error')
		}
	}

	const handleRemove = async (id: string) => {
		try {
			await removeAppInstance(id)
			mutateAppInstance((instances) => instances?.filter(({ appId }) => appId !== id), { revalidate: false })
			track(EVENT_NAMES.APPS_REMOVE_CLICKED)
		} catch {
			showToast('error')
		}
	}

	const handleAddAppWithProps = async (properties: AppInstance['properties']) => {
		if (!selectedApp) {
			return
		}

		try {
			const data = await addAppInstance(selectedApp.id, properties)
			mutateAppInstance((instances) => [...(instances ?? []), data], { revalidate: false })
			track(EVENT_NAMES.APPS_ADD_WITH_PROPERTIES_CLICKED)
			showInPreview()
		} catch {
			showToast('error')
		} finally {
			setSelectedApp(null)
		}
	}

	if (!apps || !appInstances) {
		return null
	}

	return (
		<Wrapper>
			<VideoSection>
				<Video src="/videos/AppsIntegration.mp4" />
			</VideoSection>
			<Explanation>
				{`Apps is a gallery of applications tailored to your customer's needs. Enhance OpenWidget with community-based features like package tracking, scheduling calls and much more.`}
			</Explanation>
			<Explanation>
				Want to add your own app?{' '}
				<StyledLink target="_blank" href="https://openwidget.com/developers/openwidget-apps">
					Click here
				</StyledLink>
			</Explanation>
			<AppsListOuter>
				<Title>Choose app</Title>
				{areAppsLoading || areAppInstancesLoading ? (
					<CircularLoader size="xlarge" />
				) : (
					<>
						{apps.map(({ id, description, name, icon, externalUrl }) => {
							const formattedSelectionAppDate = formatDate(
								appInstances?.find(({ appId }) => appId === id)?.createdAt ?? '',
							)
							return (
								<AppItemOuter key={id} data-testid="app-item">
									<TileHeader>
										<LogoOuter>
											<StyledImage src={icon} alt={name} width={20} height={20} />
											<AppName>{name}</AppName>
										</LogoOuter>
										{isAppInstalled(id) ? (
											<UseAsyncFn key={`remove-app-${id}`} asyncFn={() => handleRemove(id)}>
												{({ loading, callAsyncFn }) => (
													<StyledButton loading={loading} onClick={callAsyncFn} kind="destructive-text" disableHover>
														Remove
													</StyledButton>
												)}
											</UseAsyncFn>
										) : (
											<UseAsyncFn key={`add-app-${id}`} asyncFn={() => handleAdd(id)}>
												{({ loading, callAsyncFn }) => (
													<StyledButton loading={loading} onClick={callAsyncFn} kind="text" disableHover>
														Add +
													</StyledButton>
												)}
											</UseAsyncFn>
										)}
									</TileHeader>
									<TileContent>
										<AppDescription>{description}</AppDescription>
									</TileContent>
									<TileFooter>
										{isAppInstalled(id) && (
											<StatusContainer>
												<StatusCircle />
												<StatusText>{`Installed: ${formattedSelectionAppDate.day}, ${formattedSelectionAppDate.time}`}</StatusText>
											</StatusContainer>
										)}
										{externalUrl && (
											<StyledLink href={externalUrl} target="_blank" rel="noopener,noreferrer">
												Learn more
											</StyledLink>
										)}
									</TileFooter>
								</AppItemOuter>
							)
						})}
					</>
				)}
			</AppsListOuter>
			{selectedApp && (
				<AppPropertiesModal hideModal={hideModal}>
					<AppPropertiesForm app={selectedApp} onSubmit={handleAddAppWithProps} onCancel={hideModal} />
				</AppPropertiesModal>
			)}
		</Wrapper>
	)
}

export default AppsIntegration
